import { batch } from 'react-redux';
import { createReducer } from '../reducers/factory';
import friendsApi from '../api/radarFriends';
import { addFriend } from './radarFriends';
import { LOGOUT_SUCCESSFUL } from '../reducers/auth';
import { plainFetchSelectorCreator } from '../utils/selectors';
import { CLEAR_RADAR_USER_DATA } from './radarUserProfile';

const reducerName = 'radarInvitations';

const fetchReducer = createReducer('INVITATIONS', {
  fetchTimeout: 30 * 1000,
  invalidateActions: ['LOGIN_SUCCESSFUL'],
  minUpdate: 5 * 60 * 1000,
  initialState: {
    // ordered from oldest to newest
    items: [],
  },
});

const SET_SEARCHED_FRIEND = 'SET_SEARCHED_FRIEND';
const ADD_INVITATION = 'ADD_INVITATION';
const REMOVE_INVITATION = 'REMOVE_INVITATION';
const SYNCHRONIZE_INVITATIONS = 'FETCH_SUCCESSFUL_AND_UPDATE_SEARCH';
const ACKNOWLEDGE_FRIENDS_ENTRY = 'ACKNOWLEDGE_FRIENDS_ENTRY';

const InvitationsReducer = (
  state = fetchReducer.INITIAL_STATE,
  action = {},
) => {
  switch (action.type) {
    case LOGOUT_SUCCESSFUL:
    case CLEAR_RADAR_USER_DATA:
      return { ...fetchReducer.INITIAL_STATE };
    case SYNCHRONIZE_INVITATIONS: {
      return {
        ...state,
        items: action.items,
        fetchingInProgressSince: null,
        lastSuccessfulFetch: action.timestamp,
        fetchPromise: null,
        didInvalidate: false,
        searchedFriend: action.friend,
        searchedFriendCode: action.code,
      };
    }
    case ADD_INVITATION:
      return {
        ...state,
        items: [...state.items, action.invitation],
        searchedFriendCode: '',
        searchedFriend: null,
      };
    case REMOVE_INVITATION: {
      // clear search if duplicate of existing row in list
      const clearSearch = state.searchedFriend?.id === action.id;
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.id),
        searchedFriendCode: clearSearch ? '' : state.searchedFriendCode,
        searchedFriend: clearSearch ? '' : state.searchedFriend,
      };
    }
    case SET_SEARCHED_FRIEND:
      return {
        ...state,
        searchedFriend: action.friend,
      };
    case ACKNOWLEDGE_FRIENDS_ENTRY: {
      return {
        ...state,
        items: state.items.map((item) =>
          action.invites.includes(item.id)
            ? { ...item, acknowledged: true }
            : item,
        ),
      };
    }
    default:
      return fetchReducer(state, action);
  }
};

export const invitationsPlainSelector = plainFetchSelectorCreator(reducerName);
export const invitationsSelector = ({ [reducerName]: { items } }) =>
  items || [];

export const addInvitation = (invitation) => ({
  type: ADD_INVITATION,
  invitation,
});
export const removeInvitation = (id) => ({ type: REMOVE_INVITATION, id });
const synchronizeInvitations = (items, timestamp, friend, code) => ({
  type: SYNCHRONIZE_INVITATIONS,
  items,
  timestamp,
  friend,
  code,
});

const fetchInvitationsAction = () => async (dispatch, getState) => {
  const {
    auth: { token },
    [reducerName]: { searchedFriend },
  } = getState();
  if (!token) return [];

  const fetchPromise = (async () => {
    try {
      let availableInvitation = null;
      const fetchedInvitations = await friendsApi.fetchInvitations(token);

      const searchedCuid =
        searchedFriend?.cuid || // stranger or friend
        searchedFriend?.user?.cuid || // invitation (to me)
        searchedFriend?.invitee?.cuid; // invitation (from me)

      // searched user changed to invitation
      if (searchedCuid) {
        availableInvitation = fetchedInvitations.find(
          (el) =>
            el?.user?.cuid === searchedCuid ||
            el?.invitee?.cuid === searchedCuid,
        );
      }

      // clear if searched user available in the list or was available before fetching
      if (availableInvitation || searchedFriend?.duplicate) {
        dispatch(
          synchronizeInvitations(fetchedInvitations, Date.now(), null, ''),
        );
      } else {
        dispatch(fetchReducer.fetchSuccessful(fetchedInvitations));
      }
    } catch (error) {
      dispatch(fetchReducer.fetchFailed(error));
    }
  })();

  dispatch(fetchReducer.fetchInProgress(fetchPromise));
  return fetchPromise;
};

export const fetchInvitationsIfNeeded =
  (forceUpdate) => async (dispatch, getState) => {
    const {
      [reducerName]: { fetchPromise, items, ...state },
    } = getState();

    if (forceUpdate || fetchReducer.shouldFetch(state)) {
      return dispatch(fetchInvitationsAction());
    }

    return fetchPromise?.then === 'function' ? fetchPromise : items;
  };

export const sendInvitation = (uid) => async (dispatch, getState) => {
  const {
    auth: { token },
  } = getState();

  if (!uid || !token) {
    return null;
  }

  const responseInvitation = await friendsApi.inviteFriend(token, uid);

  if (responseInvitation.status === 'awaiting') {
    dispatch(addInvitation(responseInvitation));
    return responseInvitation;
  }

  if (responseInvitation.status === 'rejected') {
    // return response and handle in the app with appropriate alert
    return responseInvitation;
  }

  // immediate accept - add response.friend to the list
  if (responseInvitation?.friend) {
    dispatch(addFriend(responseInvitation.friend));
  }

  return null;
};

export const rejectInvitation =
  (invitationId) => async (dispatch, getState) => {
    const {
      auth: { token },
    } = getState();

    if (!invitationId || !token) {
      return null;
    }

    await friendsApi.rejectInvitation(token, invitationId);

    // remove invitation from the list
    dispatch(removeInvitation(invitationId));

    return null;
  };

export const acceptInvitation =
  (invitationId) => async (dispatch, getState) => {
    const {
      auth: { token },
    } = getState();

    if (!invitationId || !token) {
      return null;
    }

    const responseFriend = await friendsApi.acceptInvitation(
      token,
      invitationId,
    );

    // add friend to the end of the friends list and remove from invitations list
    if (responseFriend.id) {
      batch(() => {
        dispatch(removeInvitation(invitationId));
        dispatch(addFriend(responseFriend));
      });
    }

    return responseFriend;
  };

export const deleteInvitation =
  (invitationId) => async (dispatch, getState) => {
    const {
      auth: { token },
    } = getState();

    if (!invitationId || !token) {
      return null;
    }

    await friendsApi.deleteInvitation(token, invitationId);

    dispatch(removeInvitation(invitationId));

    return null;
  };

export default {
  [reducerName]: InvitationsReducer,
};
